import axios from "../../../../../../../../api/axios";

/**
 * API endpoints for lease and tenant flow
 */
const ENDPOINTS = {
  GET_PROPERTY_DETAILS_FOR_LEASE: "v2/lease/property",         // Manager creates lease
  CREATE_LEASE: "v2/lease/",                                   // Manager creates lease
  SEND_LEASE_TO_TENANT: "v2/lease/send-to-tenant",             // Send lease to tenant's email
  GET_ACTIVE_LEASE_BY_UNIT_NO: "v2/lease/active",              // Fetch lease info by ID
  GET_LEASE_BY_ID: "v2/lease/get-lease-by-id/:id",             // Fetch lease info by ID
  UPDATE_LEASE_BY_ID: "v2/lease/update/:id/:step",
  GET_LEASE_DOCUMENTS_BY_PROPERTY_ID: "v2/lease/",             // Fetch lease info by ID
  TENANT_SIGN_LEASE: "v2/lease/tenant-sign",                   // Tenant signs the lease
  GET_LEASE_FOR_MANAGER_REVIEW: "v2/lease/pending-approval",   // Manager fetches signed lease
  MANAGER_APPROVE_LEASE: "v2/lease/manager-approve",           // Manager approves the lease
  DOWNLOAD_GENERATED_LEASE: "v2/lease/download",               // Download final lease (PDF)
  GET_LEASES_FOR_TENANT: "v2/lease/tenant-leases",             // List leases for a tenant
  GET_LEASES_FOR_MANAGER: "v2/lease/manager-leases",           // List leases for a manager
  GET_LEASES_BY_PROPERTY: "v2/lease/property/:propertyId",     // List leases for a manager
  UPDATE_LEASE_STATUSES: "v2/lease/property/:propertyId/status", // Update lease statuses for a property
  UPDATE_LEASE: "v2/lease/update",                             // Update draft lease
  ATTACH_LEASE_DOCUMENTS: "v2/lease/:lease_id/documents",      // Update draft lease
  DELETE_LEASE: "v2/lease/delete",                             // Delete draft lease
  GET_LEASE_STATUS: "v2/lease/status",
  GET_SIGNED_URL: "v2/lease/document/signed-url",
  GET_LEASE_HISTORY: "v2/lease/history",
  SEND_SIGNING_LINKS_TO_TENANTS: "v2/lease/:lease_id/send-signing-links",
  GET_SIGN_REMINDER_HISTORY: "v2/lease/:lease_id/sign-reminders",
  ARCHIEVE_LEASE: "v2/lease/archieve/:id",
  DELETE_LEASE_PERMANENTLY: "v2/lease/permanent/:id",
  SET_LEASE_KIND: "v2/lease/:id/kind",                         // New lease or renewal
};

/**
 * Record whether an in-progress lease is a new lease or a renewal.
 * @param {string} leaseId
 * @param {"NEW"|"RENEWAL"} leaseKind
 */
export const setLeaseKind = async (leaseId, leaseKind) => {
  const response = await axios.patch(ENDPOINTS.SET_LEASE_KIND.replace(':id', leaseId), { lease_kind: leaseKind });
  return response.data;
};

export const toggleLeaseStatus = async (leaseId, isActive) => {
  const response = await axios.patch(`/api/leases/${leaseId}/status`, { isActive });
  return response.data;
};

/**
 * Get lease details by ID
 * @param {string} leaseId
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>} Lease object
 */
export const getLeaseByIdForTenantSignature = async (leaseId, token) => {
  const url = ENDPOINTS.GET_LEASE_BY_ID.replace(':id', leaseId);

  const response = await axios.get(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response.data;
};

/**
 * Helper to update a lease by ID with partial data
 * @param {string} leaseId - ID of the lease to update
 * @param {Object} updatedData - Partial lease data to update
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>} Updated lease data
 * @throws {Error} If the update fails or parameters are invalid
 */
export const updateLeaseById = async (leaseId, step, updatedData) => {
  try {
    if (!leaseId) {
      throw new Error('leaseId is required');
    }
    if (!updatedData || Object.keys(updatedData).length === 0) {
      throw new Error('updatedData is required and cannot be empty');
    }
    const url = ENDPOINTS.UPDATE_LEASE_BY_ID.replace(':id', leaseId).replace(':step', step);
    const response = await axios.patch(url, updatedData, {
    });
    return response.data;
  } catch (error) {
    console.log({ error })
    throw new Error(error.response?.data?.message || 'Failed to update lease');
  }
};

/**
 * Get the status of a lease by unit and property
 * @param {Object} params - Query parameters { property_id, unit_no, building_name }
 * @returns {Promise<Object>} Lease status data
 */
export const getLeaseStatus = async (params) => {
  const response = await axios.get(ENDPOINTS.GET_LEASE_STATUS, { params });
  return response.data;
};

export const getLeaseHistory = async (params) => {
  const {
    property_id,
    building_name,
    unit_no,
    status,
    start_date,
    end_date,
    sort_by = 'lease_details.start_date',
    sort_order = 'desc',
    page = '1',
    limit = '10',
    include_archived = 'false',
    include_documents = 'false',
    include_tenant_details = 'false',
    include_signature_status = 'false'
  } = params;

  const queryParams = {
    property_id,
    building_name,
    unit_no,
    status,
    start_date,
    end_date,
    sort_by,
    sort_order,
    page,
    limit,
    include_archived,
    include_documents,
    include_tenant_details,
    include_signature_status
  };

  const response = await axios.get(ENDPOINTS.GET_LEASE_HISTORY, { params: queryParams });
  return response.data;
};

export const getSignedUrl = async (document) => {
  const params = { key: document.key };
  const { data: { data: response } } = await axios.get(ENDPOINTS.GET_SIGNED_URL, { params });
  return response;
};

/**
 * Create a new lease by manager
 * @param {Object} payload - Lease form data
 * @returns {Promise<Object>} Created lease data
 */
export const createLease = async (payload) => {
  const response = await axios.post(ENDPOINTS.CREATE_LEASE, payload);
  return response.data;
};

/**
 * Attach documents to a specific lease
 * @param {string} leaseId - ID of the lease to attach documents to
 * @param {FormData} values - FormData object containing the document files
 * @returns {Promise<Object>} Response data from the API
 */
export const attachDocumentsToLease = async (leaseId, values) => {
  try {
    const url = ENDPOINTS.ATTACH_LEASE_DOCUMENTS.replace(':lease_id', leaseId);
    const response = await axios.put(url, values);
    return response.data;
  } catch (error) {
    console.error('Error attaching documents to lease:', error);
    throw error;
  }
};

/**
 * Send signing links to tenants for a specific lease
 * @param {string} leaseId - ID of the lease
 * @param {Array<Object>} tenants - Array of tenant objects with email and other info
 * @returns {Promise<Object>} Response data from the API
 */
export const sendSigningLinksToTenants = async (leaseId, values) => {
  try {
    const url = ENDPOINTS.SEND_SIGNING_LINKS_TO_TENANTS.replace(':lease_id', leaseId);
    const response = await axios.post(url, { tenants: values.tenants });
    return response.data;
  } catch (error) {
    console.error('Error sending signing links to tenants:', error);
    throw error;
  }
};

/**
 * Automatic signing reminders sent for a lease, newest first
 * @param {string} leaseId
 * @returns {Promise<Array<{ stepKey, stepLabel, tenantName, email, sentAt }>>}
 */
export const getSignReminderHistory = async (leaseId) => {
  const url = ENDPOINTS.GET_SIGN_REMINDER_HISTORY.replace(':lease_id', leaseId);
  const response = await axios.get(url);
  return response.data?.data || [];
};

/**
 * Send lease to tenant's email
 * @param {string} leaseId - ID of the lease to send
 * @returns {Promise<Object>} Status of email dispatch
 */
export const sendLeaseToTenant = async (leaseId) => {
  const response = await axios.post(`${ENDPOINTS.SEND_LEASE_TO_TENANT}/${leaseId}`);
  return response.data;
};

/**
 * Get active leases by property_id, optional manager_id, and optional building_name
 * @param {string} propertyId - The property ID (required)
 * @param {string} [managerId] - The manager ID (optional)
 * @param {string} [buildingName] - The building name (optional)
 * @returns {Promise<Object>} Active property details
 */
export const getPropertyDetailsForLease = async (propertyId, buildingName = '') => {
  if (!propertyId) {
    throw new Error('propertyId is required');
  }

  const queryParams = new URLSearchParams({ property_id: propertyId });

  if (buildingName) {
    queryParams.append('building_name', buildingName);
  }

  const response = await axios.get(
    `${ENDPOINTS.GET_PROPERTY_DETAILS_FOR_LEASE}?${queryParams.toString()}`
  );

  return response.data;
};

/**
 * Get lease documents by property ID
 * @param {string} propertyId - ID of the property
 * @returns {Promise<Object>} - Signed lease documents
 */
export const getLeaseDocumentsByPropertyId = async (propertyId) => {
  const response = await axios.get(`${ENDPOINTS.GET_LEASE_DOCUMENTS_BY_PROPERTY_ID}${propertyId}/documents`);
  return response.data;
};

/**
 * Get active leases by property_id, optional building_name, and unit_no
 * @param {string} propertyId - The property ID
 * @param {string} unitNo - The unit number
 * @param {string} [buildingName] - The building name (optional)
 * @returns {Promise<Object>} Active lease details
 */
export const getActiveLeaseByUnitNo = async (propertyId, buildingName = '', unitNo) => {
  const queryParams = new URLSearchParams({
    property_id: propertyId,
    unit_no: unitNo,
  });

  if (buildingName) {
    queryParams.append('building_name', buildingName);
  }
  const response = await axios.get(`${ENDPOINTS.GET_ACTIVE_LEASE_BY_UNIT_NO}?${queryParams.toString()}`);
  return response.data;
};

/**
 * Tenant signs a lease
 * @param {string} leaseId
 * @param {Object} signatureData - Signature or sign confirmation
 * @returns {Promise<Object>} Status after signing
 */
export const tenantSignLease = async (leaseId, signatureData) => {
  const response = await axios.post(`${ENDPOINTS.TENANT_SIGN_LEASE}/${leaseId}`, signatureData);
  return response.data;
};

/**
 * Get lease pending approval for manager review
 * @returns {Promise<Array>} Leases awaiting approval
 */
export const getLeasesPendingManagerReview = async () => {
  const response = await axios.get(ENDPOINTS.GET_LEASE_FOR_MANAGER_REVIEW);
  return response.data;
};

/**
 * Manager approves signed lease
 * @param {string} leaseId
 * @returns {Promise<Object>} Approval result
 */
export const managerApproveLease = async (leaseId) => {
  const response = await axios.post(`${ENDPOINTS.MANAGER_APPROVE_LEASE}/${leaseId}`);
  return response.data;
};

/**
 * Download final lease document (PDF)
 * @param {string} leaseId
 * @returns {Promise<Blob>} Lease file
 */
export const downloadGeneratedLease = async (leaseId) => {
  const response = await axios.get(`${ENDPOINTS.DOWNLOAD_GENERATED_LEASE}/${leaseId}`, {
    responseType: "blob",
  });
  return response.data;
};

/**
 * Get all leases assigned to a tenant
 * @param {string} tenantId
 * @returns {Promise<Array>} Lease list
 */
export const getLeasesForTenant = async (tenantId) => {
  const response = await axios.get(`${ENDPOINTS.GET_LEASES_FOR_TENANT}/${tenantId}`);
  return response.data;
};

/**
 * Get all leases created by a manager
 * @param {string} managerId
 * @returns {Promise<Array>} Lease list
 */
export const getLeasesForManager = async (managerId) => {
  const response = await axios.get(`${ENDPOINTS.GET_LEASES_FOR_MANAGER}/${managerId}`);
  return response.data;
};

/**
 * Get all leases by property ID, building name, and unit number
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>} List of leases
 */
export const getLeasesByProperty = async (propertyId, buildingName, unitNo) => {
  if (!propertyId || !buildingName || !unitNo) {
    throw new Error('propertyId, buildingName, and unitNo are required');
  }

  const url = ENDPOINTS.GET_LEASES_BY_PROPERTY.replace(':propertyId', propertyId);
  const queryParams = new URLSearchParams({
    building_name: buildingName,
    unit_no: unitNo,
  });

  const response = await axios.get(`${url}?${queryParams.toString()}`);

  return response.data;
};

/**
 * Update lease statuses for a property and unit
 * @param {string} propertyId - The property ID
 * @param {string} buildingName - The building name
 * @param {string} unitNo - The unit number
 * @param {string} activeLeaseId - ID of the lease to set as ACTIVE
 * @param {string} otherLeaseStatus - Status to set for other leases (e.g., TERMINATED, ARCHIVED)
 * @param {string} token - JWT token for authorization
 * @returns {Promise<Object>} Update result
 */
export const updateLeaseStatuses = async (propertyId, buildingName, unitNo, activeLeaseId, otherLeaseStatus, leaseType) => {
  if (!propertyId || !buildingName || !unitNo || !activeLeaseId || !otherLeaseStatus || !leaseType) {
    throw new Error('propertyId, buildingName, unitNo, activeLeaseId, otherLeaseStatus, and leaseType are required');
  }

  const url = ENDPOINTS.UPDATE_LEASE_STATUSES.replace(':propertyId', propertyId);
  const payload = {
    building_name: buildingName,
    unit_no: unitNo,
    active_lease_id: activeLeaseId,
    other_lease_status: otherLeaseStatus,
    lease_type: leaseType, 
  };

  const response = await axios.patch(url, payload);
  return response.data;
};

/**
 * Update an existing draft lease
 * @param {string} leaseId
 * @param {Object} updatedData
 * @returns {Promise<Object>} Updated lease info
 */
export const updateLease = async (leaseId, updatedData) => {
  const response = await axios.put(`${ENDPOINTS.UPDATE_LEASE}/${leaseId}`, updatedData);
  return response.data;
};

/**
 * Delete a draft lease
 * @param {string} leaseId
 * @returns {Promise<Object>} Deletion status
 */
export const deleteLease = async (leaseId) => {
  const response = await axios.delete(`${ENDPOINTS.DELETE_LEASE}/${leaseId}`);
  return response.data;
};

/**
 * Archives a lease by its ID 
 * @param {string} leaseId - The ID of the lease to archive
 * @param {string} token - JWT token for authorization
 * @returns {Promise<any>} - The response from the server
 */
export const archiveLeaseById = async (leaseId, token) => {
  const url = ENDPOINTS.ARCHIEVE_LEASE.replace(':id', leaseId);

  const response = await axios.post(url, {}, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response.data;
};

/**
 * Permanently deletes a lease by its ID
 * @param {string} leaseId - The ID of the lease to delete
 * @returns {Promise<any>} - The response from the server
 */
export const deleteLeaseById = async (leaseId) => {
  const url = ENDPOINTS.DELETE_LEASE_PERMANENTLY.replace(':id', leaseId);
  const response = await axios.delete(url);
  return response.data;
};